import { useSubscription } from "../contexts/subscription";
import { isComplimentaryPracticeTrack, useTrack } from "../contexts/track";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { AlertTriangle, CheckCircle2, XCircle, Zap, Trophy, Target, BarChart3, Clock, BookOpen, TrendingUp } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import { fetchWithAuth } from "@/lib/api";
import logoUrl from "../assets/logo.png";
import bgNightUrl from "../assets/bg-night.png";

const PRICES = {
  pro: {
    monthly: { display: "CA$14.99" },
    yearly: { display: "CA$143.88", perMonth: "CA$11.99", saving: "Save CA$36/yr" },
  },
  pro_plus: {
    monthly: { display: "CA$24.99" },
    yearly: { display: "CA$239.88", perMonth: "CA$19.99", saving: "Save CA$60/yr" },
  },
} as const;

type SubscriptionStyle = "classic" | "premium" | "platinum";

export default function Subscription() {
  const { getPlanForTrack, loading, startCheckout, openPortal, isAdmin } = useSubscription();
  const { selectedTrack } = useTrack();
  const { t, language } = useLanguage();
  const s = t.subscription;
  /** Billing and Pro/Pro+ features apply to CPL only; other banks are complimentary in-app. */
  const actualPlan = selectedTrack === "cpl" ? getPlanForTrack("cpl") : "free";
  const isOnboardingFlow = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("from") === "onboarding";
  const showAdminPreview = isAdmin && !isOnboardingFlow;
  const plan = showAdminPreview ? "free" : actualPlan;
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [interval, setInterval] = useState<"month" | "year">("month");
  const [subscriptionStyle, setSubscriptionStyle] = useState<SubscriptionStyle>("premium");

  const yearly = interval === "year";
  const pro = PRICES.pro[yearly ? "yearly" : "monthly"];
  const proPlus = PRICES.pro_plus[yearly ? "yearly" : "monthly"];
  const isFree = plan === "free";
  const isPro = plan === "pro";
  const isProPlus = plan === "pro_plus";
  const planLabel = isProPlus ? "PRO PLUS ADVANCED" : isPro ? "PRO" : "BASIC (FREE)";

  const complimentaryTrackNotice = isComplimentaryPracticeTrack(selectedTrack) ? (
    <div className="mb-6 rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-xs text-sky-100/95 font-mono text-center max-w-2xl mx-auto leading-relaxed">
      {language === "fr"
        ? "Les banques PPL, PSTAR et ROC-A sont incluses sans frais. Les forfaits ci-dessous concernent uniquement la préparation LPC (CPL)."
        : "PPL, PSTAR, and ROC-A question banks are included at no charge. Plans below are for CPL Commercial Pilot preparation only."}
    </div>
  ) : null;

  useEffect(() => {
    let active = true;
    fetchWithAuth<{ subscriptionStyle?: string }>("/api/tracks/enabled")
      .then((cfg) => {
        if (!active) return;
        const style = cfg?.subscriptionStyle;
        setSubscriptionStyle(style === "classic" || style === "platinum" ? style : "premium");
      })
      .catch(() => active && setSubscriptionStyle("premium"));
    return () => { active = false; };
  }, []);

  async function handleCheckout(targetPlan: "pro" | "pro_plus") {
    if (showAdminPreview) return;
    setWorking(targetPlan);
    setError(null);
    try {
      await startCheckout(targetPlan, "cpl", interval);
    } catch (e: unknown) {
      setError((e as Error).message ?? s.errorCheckout);
      setWorking(null);
    }
  }

  async function handlePortal() {
    if (showAdminPreview) return;
    setWorking("portal");
    setError(null);
    try {
      await openPortal();
    } catch (e: unknown) {
      setError((e as Error).message ?? s.errorPortal);
      setWorking(null);
    }
  }

  const FREE_FEATURES = [
    { text: t.lang === "fr" ? "Les 4 sections d'examen" : "All 4 exam sections", ok: true },
    { text: t.lang === "fr" ? "Jusqu'à 40 questions par section (160 au total)" : "Up to 40 questions per section (160 total)", ok: true },
    { text: t.lang === "fr" ? "Questions non rotatives" : "Non-rotational questions", ok: true },
    { text: t.lang === "fr" ? "Suivi de base des progrès" : "Basic progress tracking", ok: true },
    { text: t.lang === "fr" ? "Simulations d'examen" : "Exam simulations", ok: false },
    { text: t.lang === "fr" ? "Entraînement aux faiblesses" : "Weakness training", ok: false },
  ];
  const PRO_FEATURES_YES = t.lang === "fr"
    ? ["Tout le plan de base +", "Jusqu'à 400 questions (non rotatives)", "Modes de pratique aléatoire et par catégorie", "Suivi de base des progrès"]
    : ["Everything in Basic +", "Up to 400 questions (non-rotational)", "Random & category practice modes", "Basic progress tracking"];
  const PRO_FEATURES_NO = t.lang === "fr"
    ? ["Aucune simulation d'examen", "Aucun entraînement aux faiblesses", "Aucune analyse avancée", "Aucun Score de préparation", "Couverture incomplète — lacunes probables"]
    : ["No exam simulations", "No weakness training", "No advanced analytics", "No Exam Readiness Score", "Incomplete coverage — knowledge gaps likely"];
  const PRO_PLUS_FEATURES = t.lang === "fr"
    ? [
        { icon: BookOpen, text: "Plus de 5 000 questions — bassin complet et rotatif" },
        { icon: Clock, text: "Simulations CPAER chronométrées — 100 questions / 3 heures" },
        { icon: Target, text: "Entraînement ciblé — travaillez vos points faibles" },
        { icon: BarChart3, text: "Analyse avancée + détail complet par catégorie" },
        { icon: TrendingUp, text: "Score de préparation avec prédiction de réussite" },
        { icon: Trophy, text: "Soutien prioritaire — conçu pour réussir du premier coup" },
      ]
    : [
        { icon: BookOpen, text: "5,000+ question bank — full rotating pool" },
        { icon: Clock, text: "Real CPAER-style timed simulations — 100 questions / 3 hours" },
        { icon: Target, text: "Weakness-based training — drill your problem areas" },
        { icon: BarChart3, text: "Advanced analytics + full category breakdown" },
        { icon: TrendingUp, text: "Exam Readiness Score with pass prediction" },
        { icon: Trophy, text: "Priority support — built to pass on the first attempt" },
      ];

  const renderBaseCards = (mode: "classic" | "premium") => {
    const showFancy = mode !== "classic";
    return (
      <>
        {error && <p className="text-destructive text-xs mb-4 text-center">{error}</p>}
        <div className="grid lg:grid-cols-3 gap-5 items-stretch">
          <div className={`rounded-2xl border p-6 flex flex-col ${showFancy ? "shadow-xl" : ""} ${isFree ? "border-border/70 bg-card/90" : "border-border/30 bg-secondary/30"}`}>
            <div className="mb-4">
              <div className="text-muted-foreground text-xs tracking-widest mb-1">STARTER</div>
              <div className="text-2xl font-bold text-foreground">BASIC <span className="text-muted-foreground text-lg">(FREE)</span></div>
              <div className="text-muted-foreground text-xs mt-1">{s.noCardNeeded}</div>
            </div>
            <ul className="space-y-2 mb-6 flex-1">
              {FREE_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className={f.ok ? "text-primary mt-0.5" : "text-muted-foreground/30 mt-0.5"}>{f.ok ? "✓" : "✗"}</span>
                  <span className={f.ok ? "text-foreground/70" : "text-muted-foreground/40"}>{f.text}</span>
                </li>
              ))}
            </ul>
            <div className="py-2 text-center text-muted-foreground text-xs border border-border rounded-lg mt-auto bg-secondary/30">
              {isFree ? s.currentPlanBadge : s.downgradePortal}
            </div>
          </div>

          <div className={`rounded-2xl border p-6 flex flex-col relative overflow-hidden ${showFancy ? "shadow-xl" : ""} ${isPro ? "border-primary/40 bg-primary/10" : "border-border/30 bg-card/70"}`}>
            {isFree && <div className="absolute top-3 right-3 bg-secondary text-muted-foreground text-[10px] font-bold px-2 py-0.5 rounded tracking-widest">{s.practiceOnly}</div>}
            <div className="mb-3">
              <div className="text-muted-foreground text-xs tracking-widest mb-1">CPL PREP PRO</div>
              <div className="flex items-end gap-1.5">
                <div className="text-2xl font-bold text-foreground">{yearly ? PRICES.pro.yearly.perMonth : PRICES.pro.monthly.display}</div>
                <div className="text-muted-foreground text-xs mb-1">{s.perMonth}</div>
              </div>
              {yearly ? (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-muted-foreground text-xs">{PRICES.pro.yearly.display} {s.billedAnnually}</span>
                  <span className="bg-primary/15 text-primary text-[10px] font-bold px-1.5 py-0.5 rounded">{PRICES.pro.yearly.saving}</span>
                </div>
              ) : <div className="text-muted-foreground text-xs mt-1">{s.cancelAnytime}</div>}
            </div>
            <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/25 rounded-lg p-3 mb-4">
              <AlertTriangle className="w-3.5 h-3.5 text-orange-400 shrink-0 mt-0.5" />
              <p className="text-orange-400/90 text-[11px] leading-relaxed">{s.practiceOnlyWarning}</p>
            </div>
            <div className="space-y-3 mb-5 flex-1">
              <div>
                <p className="text-muted-foreground text-[10px] uppercase tracking-widest mb-1.5">{s.included}</p>
                <ul className="space-y-1.5">
                  {PRO_FEATURES_YES.map((f, i) => <li key={i} className="flex items-start gap-2 text-xs"><CheckCircle2 className="w-3 h-3 text-primary/70 shrink-0 mt-0.5" /><span className="text-foreground/65">{f}</span></li>)}
                </ul>
              </div>
              <div>
                <p className="text-muted-foreground/50 text-[10px] uppercase tracking-widest mb-1.5">{s.notIncluded}</p>
                <ul className="space-y-1.5">
                  {PRO_FEATURES_NO.map((f, i) => <li key={i} className="flex items-start gap-2 text-xs"><XCircle className="w-3 h-3 text-muted-foreground/30 shrink-0 mt-0.5" /><span className="text-muted-foreground/40">{f}</span></li>)}
                </ul>
              </div>
            </div>
            <div className="mt-auto">
              {isPro ? (
                <button onClick={handlePortal} disabled={!!working || loading} className="w-full py-2.5 border border-primary/30 text-primary/70 text-xs font-bold rounded-lg hover:bg-primary/10 transition-colors disabled:opacity-50">{working === "portal" ? s.loading : s.manageSubscription}</button>
              ) : isProPlus ? (
                <div className="py-2 text-center text-muted-foreground/40 text-xs border border-border/40 rounded-lg">{s.includedIn}</div>
              ) : (
                <button onClick={() => handleCheckout("pro")} disabled={!!working || loading} className="w-full py-2.5 border border-border text-foreground/60 text-xs font-bold rounded-lg hover:bg-secondary transition-colors disabled:opacity-50">
                  {working === "pro" ? s.loading : `${s.startPracticing} – ${yearly ? PRICES.pro.yearly.perMonth + s.perMonth : PRICES.pro.monthly.display}`}
                </button>
              )}
            </div>
          </div>

          <div className={`rounded-2xl border-2 p-6 flex flex-col relative overflow-hidden ${showFancy ? "shadow-2xl" : ""} border-amber-400/50 bg-gradient-to-b from-amber-400/[0.06] to-amber-900/[0.04] shadow-amber-400/15`}>
            {!isProPlus && <div className="absolute top-0 right-0"><div className="text-[10px] font-black px-3 py-1 rounded-bl-lg tracking-widest bg-amber-400 text-black">{s.examReady}</div></div>}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="w-3.5 h-3.5 text-amber-400" />
                <div className="text-xs tracking-widest font-bold text-amber-400/80">PRO PLUS ADVANCED</div>
              </div>
              <div className="flex items-end gap-1.5">
                <div className="text-3xl font-black text-amber-300">{yearly ? PRICES.pro_plus.yearly.perMonth : PRICES.pro_plus.monthly.display}</div>
                <div className="text-muted-foreground/60 text-sm mb-1">{s.perMonth}</div>
              </div>
              {yearly ? (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-muted-foreground text-xs">{PRICES.pro_plus.yearly.display} {s.billedAnnually}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-300">{PRICES.pro_plus.yearly.saving}</span>
                </div>
              ) : <div className="text-foreground/40 text-xs mt-1">{s.cancelAnytime}</div>}
            </div>
            <p className="text-xs mb-4 leading-relaxed border-l-2 pl-3 text-amber-200/60 border-amber-400/30">{s.proCompleteDesc}</p>
            <ul className="space-y-2.5 mb-6 flex-1">
              {PRO_PLUS_FEATURES.map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-2.5 text-xs">
                  <Icon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
                  <span className="text-foreground/85">{text}</span>
                </li>
              ))}
            </ul>
            <div className="border rounded-lg p-3 mb-4 flex items-center gap-2 bg-amber-400/10 border-amber-400/25">
              <Zap className="w-4 h-4 text-amber-400 shrink-0" />
              <p className="text-[11px] leading-relaxed font-bold text-amber-300/90">{s.examReadyScore}</p>
            </div>
            <div className="mt-auto">
              {isProPlus ? (
                <button onClick={handlePortal} disabled={!!working || loading} className="w-full py-3.5 border text-sm font-bold rounded-lg transition-colors disabled:opacity-50 border-amber-400/40 text-amber-300 hover:bg-amber-400/10">
                  {working === "portal" ? s.loading : s.manageSubscription}
                </button>
              ) : (
                <button onClick={() => handleCheckout("pro_plus")} disabled={!!working || loading} className="w-full py-4 text-black text-sm font-black rounded-lg transition-colors disabled:opacity-50 tracking-wide shadow-lg bg-amber-400 hover:bg-amber-300 active:bg-amber-500 shadow-amber-400/25">
                  {working === "pro_plus" ? s.loading : `${s.passYourExam} — ${yearly ? PRICES.pro_plus.yearly.perMonth : PRICES.pro_plus.monthly.display}`}
                </button>
              )}
              {!isProPlus && <p className="text-muted-foreground/40 text-[10px] text-center mt-2">{s.designedToPass}</p>}
            </div>
          </div>
        </div>
      </>
    );
  };

  if (subscriptionStyle === "classic") {
    return (
      <div className="min-h-screen bg-background text-foreground font-mono px-4 py-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <Link href="/" className="flex items-center group"><img src={logoUrl} alt="Flight Deck" className="h-24 w-auto" /></Link>
            <Link href="/" className="text-xs font-mono text-muted-foreground hover:text-foreground transition-colors">{s.backToDashboard}</Link>
          </div>
          <div className="text-center mb-8">
            <img src={logoUrl} alt="Flight Deck" className="max-w-[18rem] w-full mx-auto drop-shadow-xl mb-5" />
            <h1 className="text-2xl font-bold tracking-wider text-foreground mb-2">{s.title}</h1>
            <p className="text-muted-foreground text-sm max-w-lg mx-auto leading-relaxed">
              {s.subtitle}<br /><span className="text-amber-400/90">{s.subtitle2}</span>
            </p>
          </div>
          {complimentaryTrackNotice}
          {showAdminPreview && <div className="mb-6 flex items-center justify-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/30 rounded-lg text-xs font-mono text-violet-400 tracking-wide"><span className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />{s.platinumAdminPreview}</div>}
          {!loading && !showAdminPreview && <div className="text-center mb-6"><span className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-widest border ${isProPlus ? "border-amber-400/50 bg-amber-400/10 text-amber-400" : isPro ? "border-primary/40 bg-primary/10 text-primary" : "border-border bg-secondary text-muted-foreground"}`}>{s.currentPlan} {planLabel}</span></div>}
          <div className="flex items-center justify-center mb-8">
            <div className="relative flex items-center bg-secondary border border-border rounded-xl p-1 gap-1">
              <button onClick={() => setInterval("month")} className={`px-5 py-2 rounded-lg text-xs font-bold tracking-widest transition-all duration-200 ${!yearly ? "bg-card text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>{s.monthly}</button>
              <button onClick={() => setInterval("year")} className={`relative px-5 py-2 rounded-lg text-xs font-bold tracking-widest transition-all duration-200 flex items-center gap-2 ${yearly ? "bg-amber-400/20 text-amber-400 shadow" : "text-muted-foreground hover:text-foreground"}`}>
                {s.annual}<span className="bg-amber-400 text-black text-[9px] font-black px-1.5 py-0.5 rounded tracking-wider">{s.save20}</span>
              </button>
            </div>
          </div>
          {renderBaseCards("classic")}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl border border-border bg-secondary/20"><p className="text-muted-foreground text-xs font-bold tracking-widest mb-2">{s.proNote}</p><p className="text-muted-foreground text-xs leading-relaxed">{s.proOnlyNote}</p></div>
            <div className="p-4 rounded-xl border border-amber-400/25 bg-amber-400/[0.04]"><p className="text-amber-400/80 text-xs font-bold tracking-widest mb-2">{s.proPlusNote}</p><p className="text-foreground/55 text-xs leading-relaxed">{s.proPlusDesc}</p></div>
          </div>
          <div className="flex flex-col items-center gap-2 mt-8">
            <p className="text-center text-muted-foreground/50 text-xs">{s.footer}</p>
            {(isPro || isProPlus) && <button onClick={handlePortal} disabled={!!working || loading} className="text-muted-foreground/40 hover:text-muted-foreground text-xs underline transition-colors">{s.manageBilling}</button>}
          </div>
        </div>
      </div>
    );
  }

  if (subscriptionStyle === "platinum") {
    const gold = "#E9C46A";
    const glassCard = "rounded-2xl border border-white/[0.12] bg-slate-950/40 backdrop-blur-2xl shadow-[0_8px_40px_-12px_rgba(0,0,0,0.65)]";
    const proPriceCta = yearly ? `${PRICES.pro.yearly.perMonth}${s.perMonth}` : PRICES.pro.monthly.display;
    const plusDisplay = yearly ? PRICES.pro_plus.yearly.perMonth : PRICES.pro_plus.monthly.display;
    const plusCtaGold = `${s.platinumSubscribeProPlus} — ${yearly ? PRICES.pro_plus.yearly.perMonth : PRICES.pro_plus.monthly.display}`;

    return (
      <div className="min-h-screen relative overflow-x-hidden text-white font-sans antialiased">
        <div className="fixed inset-0 -z-20">
          <img src={bgNightUrl} alt="" className="h-full w-full object-cover object-[center_30%]" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/55 via-[#0b1421]/88 to-slate-950" />
        </div>
        {/* Radar-style accents */}
        <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden opacity-[0.22]">
          <div className="absolute -top-10 left-4 md:left-10 w-40 h-40 rounded-full border border-sky-400/40" />
          <div className="absolute -top-6 left-8 md:left-14 w-28 h-28 rounded-full border border-sky-300/25" />
          <div className="absolute -top-10 right-4 md:right-10 w-40 h-40 rounded-full border border-sky-400/40" />
          <div className="absolute -top-6 right-8 md:right-14 w-28 h-28 rounded-full border border-sky-300/25" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 pt-8 pb-14">
          <div className="flex justify-end mb-4">
            <Link href="/" className="text-[11px] font-semibold tracking-widest text-white/50 hover:text-white/90 transition-colors">
              {s.backToDashboard}
            </Link>
          </div>

          <div className="text-center mb-10">
            <img src={logoUrl} alt="Flight Deck" className="h-20 md:h-24 w-auto mx-auto mb-6 drop-shadow-[0_0_24px_rgba(233,196,106,0.15)]" />
            <h1 className="text-2xl md:text-4xl font-bold tracking-[0.12em] md:tracking-[0.18em] text-white mb-3">
              {s.title}
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed font-medium">
              {s.subtitle}
            </p>
            <p className="text-sm md:text-base max-w-2xl mx-auto mt-2 leading-relaxed font-semibold" style={{ color: gold }}>
              {s.subtitle2}
            </p>
          </div>

          {complimentaryTrackNotice}

          {showAdminPreview && (
            <div className="mb-8 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-violet-950/70 border border-violet-500/35 text-[11px] font-semibold tracking-wide text-violet-200/95">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
              {s.platinumAdminPreview}
            </div>
          )}

          {!loading && !showAdminPreview && (
            <div className="text-center mb-8">
              <span
                className={`inline-block px-4 py-1.5 rounded-full text-[11px] font-bold tracking-widest border backdrop-blur-sm ${
                  isProPlus
                    ? "border-[#E9C46A]/45 bg-[#E9C46A]/10 text-[#E9C46A]"
                    : isPro
                      ? "border-cyan-400/35 bg-cyan-500/10 text-cyan-200"
                      : "border-white/15 bg-white/5 text-white/60"
                }`}
              >
                {s.currentPlan} {planLabel}
              </span>
            </div>
          )}

          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-xl p-1 gap-0.5 shadow-lg">
              <button
                type="button"
                onClick={() => setInterval("month")}
                className={`px-6 py-2.5 rounded-full text-[11px] font-bold tracking-[0.2em] transition-all ${
                  !yearly ? "bg-white/15 text-white shadow-inner" : "text-white/45 hover:text-white/75"
                }`}
              >
                {s.monthly}
              </button>
              <button
                type="button"
                onClick={() => setInterval("year")}
                className={`relative px-6 py-2.5 rounded-full text-[11px] font-bold tracking-[0.2em] flex items-center gap-2 transition-all ${
                  yearly ? "bg-[#E9C46A]/20 text-[#E9C46A] shadow-inner" : "text-white/45 hover:text-white/75"
                }`}
              >
                {s.annual}
                <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-[#E9C46A] text-slate-950 tracking-wide">{s.save20}</span>
              </button>
            </div>
          </div>

          {error && <p className="text-red-400 text-xs mb-4 text-center font-medium">{error}</p>}

          <div className="grid lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
            {/* Free */}
            <div className={`${glassCard} p-6 md:p-7 flex flex-col`}>
              <div className="mb-5 text-left">
                <div className="text-[10px] tracking-[0.25em] text-white/40 font-bold mb-1">STARTER</div>
                <div className="text-xl md:text-2xl font-bold text-white tracking-wide">{s.platinumStarterLabel}</div>
                <div className="text-white/50 text-xs mt-2 font-medium">{s.noCardNeeded}</div>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1 text-left">
                {FREE_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13px] leading-snug">
                    <span className={f.ok ? "text-[#E9C46A] mt-0.5 shrink-0" : "text-white/20 mt-0.5 shrink-0"}>{f.ok ? "✓" : "✗"}</span>
                    <span className={f.ok ? "text-white/75" : "text-white/30"}>{f.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto py-3 text-center text-white/35 text-[11px] font-bold tracking-widest rounded-xl border border-white/10 bg-black/20">
                {isFree ? s.currentPlanBadge : s.downgradePortal}
              </div>
            </div>

            {/* Pro — most popular */}
            <div className={`${glassCard} p-6 md:p-7 flex flex-col relative overflow-hidden ${isPro ? "ring-1 ring-cyan-400/30" : ""}`}>
              <div className="absolute top-3 right-3 bg-[#E9C46A] text-slate-950 text-[9px] font-black px-2.5 py-1 rounded tracking-[0.15em] shadow-lg">
                {s.mostPopular}
              </div>
              <div className="mb-4 text-left pr-16">
                <div className="text-[10px] tracking-[0.25em] text-white/40 font-bold mb-1">{s.platinumCplPrepPro}</div>
                <div className="flex items-end gap-1.5">
                  <span className="text-2xl md:text-3xl font-bold text-white">{yearly ? PRICES.pro.yearly.perMonth : PRICES.pro.monthly.display}</span>
                  <span className="text-white/45 text-sm mb-1 font-medium">{s.perMonth}</span>
                </div>
                {yearly ? (
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <span className="text-white/40 text-xs">{PRICES.pro.yearly.display} {s.billedAnnually}</span>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#E9C46A]/20 text-[#E9C46A]">{PRICES.pro.yearly.saving}</span>
                  </div>
                ) : (
                  <p className="text-white/40 text-xs mt-1">{s.cancelAnytime}</p>
                )}
              </div>
              <div className="flex items-start gap-2 rounded-xl border p-3 mb-5" style={{ borderColor: `${gold}55`, background: "rgba(233,196,106,0.06)" }}>
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: gold }} />
                <p className="text-[12px] leading-relaxed text-white/70">{s.practiceOnlyWarning}</p>
              </div>
              <div className="space-y-4 mb-6 flex-1 text-left">
                <div>
                  <p className="text-[10px] tracking-[0.2em] text-white/35 font-bold mb-2">{s.included}</p>
                  <ul className="space-y-2">
                    {PRO_FEATURES_YES.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-[12px] text-white/70">
                        <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-white/25 flex items-center justify-center text-[8px] text-[#E9C46A]">✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] text-white/25 font-bold mb-2">{s.notIncluded}</p>
                  <ul className="space-y-2">
                    {PRO_FEATURES_NO.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-[12px] text-white/35">
                        <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-white/10 flex items-center justify-center text-[8px] text-white/20">✗</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="mt-auto">
                {isPro ? (
                  <button
                    type="button"
                    onClick={handlePortal}
                    disabled={!!working || loading}
                    className="w-full py-3 rounded-xl border border-white/20 text-white/80 text-[11px] font-bold tracking-widest hover:bg-white/5 disabled:opacity-50 transition-colors"
                  >
                    {working === "portal" ? s.loading : s.manageSubscription}
                  </button>
                ) : isProPlus ? (
                  <div className="py-3 text-center text-white/30 text-[11px] font-bold tracking-widest rounded-xl border border-white/10">{s.includedIn}</div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleCheckout("pro")}
                    disabled={!!working || loading}
                    className="w-full py-3 rounded-xl border border-white/25 text-white/85 text-[11px] font-bold tracking-widest hover:bg-white/5 disabled:opacity-50 transition-colors"
                  >
                    {working === "pro" ? s.loading : `${s.platinumSelectPlan} — ${proPriceCta}`}
                  </button>
                )}
              </div>
            </div>

            {/* Pro Plus — hero card */}
            <div
              className={`${glassCard} p-6 md:p-8 flex flex-col relative overflow-hidden ring-2 shadow-[0_0_48px_-12px_rgba(233,196,106,0.35)] ${
                isProPlus ? "ring-[#E9C46A]/70" : "ring-[#E9C46A]/45"
              }`}
            >
              {!isProPlus && (
                <div className="absolute top-0 right-0">
                  <div className="text-[10px] font-black px-3 py-1.5 rounded-bl-xl tracking-[0.2em] bg-[#E9C46A] text-slate-950 shadow-lg">{s.examReady}</div>
                </div>
              )}
              <div className="mb-4 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-4 h-4" style={{ color: gold }} />
                  <span className="text-[10px] font-black tracking-[0.2em]" style={{ color: gold }}>
                    {s.platinumProPlusTitle}
                  </span>
                </div>
                <div className="flex items-end gap-1.5">
                  <span className="text-3xl md:text-4xl font-black tracking-tight" style={{ color: gold }}>
                    {plusDisplay}
                  </span>
                  <span className="text-white/45 text-sm mb-1.5 font-medium">{s.perMonth}</span>
                </div>
                {yearly ? (
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <span className="text-white/45 text-xs">{PRICES.pro_plus.yearly.display} {s.billedAnnually}</span>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#E9C46A]/20 text-[#E9C46A]">{PRICES.pro_plus.yearly.saving}</span>
                  </div>
                ) : (
                  <p className="text-white/40 text-xs mt-1">{s.cancelAnytime}</p>
                )}
              </div>
              <div className="rounded-xl border p-3.5 mb-5 text-left" style={{ borderColor: `${gold}66`, background: "rgba(233,196,106,0.07)" }}>
                <p className="text-[12px] md:text-[13px] leading-relaxed text-white/85">{s.proCompleteDesc}</p>
              </div>
              <ul className="space-y-2.5 mb-5 flex-1 text-left">
                {PRO_PLUS_FEATURES.map(({ icon: Icon, text }, i) => (
                  <li key={i} className="flex items-start gap-3 text-[12px] md:text-[13px]">
                    <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: gold }} />
                    <span className="text-white/90 leading-snug">{text}</span>
                  </li>
                ))}
              </ul>
              <div className="rounded-xl border p-3 mb-6 flex items-start gap-2.5" style={{ borderColor: `${gold}55`, background: "rgba(233,196,106,0.08)" }}>
                <Zap className="w-4 h-4 shrink-0 mt-0.5" style={{ color: gold }} />
                <p className="text-[11px] md:text-[12px] leading-relaxed font-bold" style={{ color: gold }}>
                  {s.examReadyScore}
                </p>
              </div>
              <div className="mt-auto space-y-2.5">
                {isProPlus ? (
                  <button
                    type="button"
                    onClick={handlePortal}
                    disabled={!!working || loading}
                    className="w-full py-3.5 rounded-xl border border-[#E9C46A]/40 text-[#E9C46A] text-xs font-bold tracking-widest hover:bg-[#E9C46A]/10 disabled:opacity-50 transition-colors"
                  >
                    {working === "portal" ? s.loading : s.manageSubscription}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleCheckout("pro_plus")}
                      disabled={!!working || loading}
                      className="w-full py-3 rounded-xl border border-teal-400/45 bg-teal-950/30 text-teal-100/95 text-[11px] font-bold tracking-[0.15em] hover:bg-teal-900/40 disabled:opacity-50 transition-colors"
                    >
                      {working === "pro_plus" ? s.loading : s.platinumSelectThisPlan}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCheckout("pro_plus")}
                      disabled={!!working || loading}
                      className="w-full py-3.5 rounded-xl text-slate-950 text-xs font-black tracking-[0.12em] shadow-lg transition-colors disabled:opacity-50 hover:brightness-105 active:brightness-95"
                      style={{ backgroundColor: gold, boxShadow: "0 12px 40px -8px rgba(233,196,106,0.45)" }}
                    >
                      {working === "pro_plus" ? s.loading : plusCtaGold}
                    </button>
                  </>
                )}
                {!isProPlus && <p className="text-center text-white/35 text-[10px] pt-1">{s.designedToPass}</p>}
              </div>
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className={`${glassCard} p-5 md:p-6 text-left`}>
              <p className="text-white/50 text-[10px] font-bold tracking-[0.2em] mb-2">{s.proNote}</p>
              <p className="text-white/65 text-xs leading-relaxed">{s.proOnlyNote}</p>
            </div>
            <div className={`${glassCard} p-5 md:p-6 text-left border-[#E9C46A]/25`} style={{ boxShadow: "inset 0 0 0 1px rgba(233,196,106,0.12)" }}>
              <p className="text-[#E9C46A]/90 text-[10px] font-bold tracking-[0.2em] mb-2">{s.proPlusNote}</p>
              <p className="text-white/75 text-xs leading-relaxed">{s.proPlusDesc}</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-3 mt-12">
            <p className="text-center text-white/40 text-[11px] max-w-lg leading-relaxed">{s.footer}</p>
            {(isPro || isProPlus) && (
              <button
                type="button"
                onClick={handlePortal}
                disabled={!!working || loading}
                className="text-white/35 hover:text-white/60 text-[11px] underline underline-offset-4 transition-colors disabled:opacity-50"
              >
                {s.manageBilling}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-mono px-4 py-10 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute -top-32 -left-20 h-80 w-80 rounded-full blur-3xl bg-primary/10" />
        <div className="absolute top-24 -right-20 h-96 w-96 rounded-full blur-3xl bg-amber-400/10" />
      </div>
      <div className="max-w-6xl mx-auto relative z-10">

        {/* Top nav */}
        <div className="flex items-center justify-between mb-10">
          <Link href="/" className="flex items-center group">
            <img src={logoUrl} alt="Flight Deck" className="h-24 w-auto" />
          </Link>
          <Link href="/" className="text-xs font-mono text-muted-foreground hover:text-foreground transition-colors">
            {s.backToDashboard}
          </Link>
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] font-bold tracking-widest mb-4 border-amber-400/30 bg-amber-400/10 text-amber-300">
            <Trophy className="w-3.5 h-3.5" />
            FLIGHT DECK PREMIUM
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-wider text-foreground mb-3">{s.title}</h1>
          <p className="text-muted-foreground text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            {s.subtitle}<br />
            <span className="font-semibold text-amber-400/90">{s.subtitle2}</span>
          </p>
        </div>

        {complimentaryTrackNotice}

        {/* Admin preview banner */}
        {showAdminPreview && (
          <div className="mb-6 flex items-center justify-center gap-2 px-4 py-2 bg-violet-500/10 border border-violet-500/30 rounded-lg text-xs font-mono text-violet-400 tracking-wide">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
            {s.platinumAdminPreview}
          </div>
        )}

        {/* Current plan badge */}
        {!loading && !showAdminPreview && (
          <div className="text-center mb-6">
            <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-widest border ${
              isProPlus ? "border-amber-400/50 bg-amber-400/10 text-amber-400"
                : isPro ? "border-primary/40 bg-primary/10 text-primary"
                : "border-border bg-secondary text-muted-foreground"
            }`}>
              {s.currentPlan} {planLabel}
            </span>
          </div>
        )}

        <div className="flex items-center justify-center mb-8">
          <div className="relative flex items-center border rounded-xl p-1 gap-1 bg-card/70 shadow-lg border-border">
            <button
              onClick={() => setInterval("month")}
              className={`px-5 py-2 rounded-lg text-xs font-bold tracking-widest transition-all duration-200 ${
                !yearly
                  ? "bg-card text-foreground shadow"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.monthly}
            </button>
            <button
              onClick={() => setInterval("year")}
              className={`relative px-5 py-2 rounded-lg text-xs font-bold tracking-widest transition-all duration-200 flex items-center gap-2 ${
                yearly
                  ? "bg-amber-400/20 text-amber-400 shadow"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.annual}
              <span className="text-black text-[9px] font-black px-1.5 py-0.5 rounded tracking-wider bg-amber-400">
                {s.save20}
              </span>
            </button>
          </div>
        </div>
        {renderBaseCards("premium")}

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-border bg-card/70">
            <p className="text-muted-foreground text-xs font-bold tracking-widest mb-2">{s.proNote}</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              {s.proOnlyNote}
            </p>
          </div>
          <div className="p-4 rounded-xl border border-amber-400/25 bg-amber-400/[0.04]">
            <p className="text-amber-400/80 text-xs font-bold tracking-widest mb-2">{s.proPlusNote}</p>
            <p className="text-foreground/55 text-xs leading-relaxed">
              {s.proPlusDesc}
            </p>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-border bg-card/60 px-4 py-3 text-center">
            <p className="text-[10px] tracking-widest text-muted-foreground mb-1">PAYMENTS</p>
            <p className="text-xs text-foreground/80">Secure checkout via Stripe</p>
          </div>
          <div className="rounded-lg border border-border bg-card/60 px-4 py-3 text-center">
            <p className="text-[10px] tracking-widest text-muted-foreground mb-1">FLEXIBLE</p>
            <p className="text-xs text-foreground/80">{s.cancelAnytime}</p>
          </div>
          <div className="rounded-lg border border-border bg-card/60 px-4 py-3 text-center">
            <p className="text-[10px] tracking-widest text-muted-foreground mb-1">SUPPORT</p>
            <p className="text-xs text-foreground/80">Priority support on Pro Plus</p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 mt-8">
          <p className="text-center text-muted-foreground/50 text-xs">
            {s.footer}
          </p>
          {(isPro || isProPlus) && (
            <button
              onClick={handlePortal}
              disabled={!!working || loading}
              className="text-muted-foreground/40 hover:text-muted-foreground text-xs underline transition-colors"
            >
              {s.manageBilling}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
